import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { personasApi } from '../../../lib/personasApi.js';
import { useAudioBlob, type AudioBtnState } from '../../../lib/audioBlobPlayer.js';
import {
  pickDefaultCoachForRegion,
  type ConvRegion,
  type TtsProviderId,
} from '@orborix/shared-types';
import { buildCoachCatalog, type CoachCardModel } from '../coachCatalog.js';
import {
  SPANISH_VARIANT_DEMO_TEXT,
  TONE_DEMO_TEXT,
  type SpanishVariantId,
  type ToneId,
} from '../conversationTypes.js';

const MODULE_SLUG = 'english';

/**
 * Estado del CoachPicker: carga el catálogo real de personas de inglés con voz
 * del provider TTS activo de speak (`GET /api/voices/active`),
 * preselecciona la voz persistida del módulo (`module_overrides.english`),
 * persiste la elección y la aplica en caliente vía `sendVoiceChange`. Reusa la
 * lógica del orbe viejo (preview + persist + cambio mid-sesión) sin acoplar a
 * `apps/client`.
 *
 * Elegir una voz reproduce su demo en el acto (auto-play, sin tap extra en
 * "Probar voz"): la elección ES el gesto del usuario, así que el navegador lo
 * permite. Nunca suena al cargar/preseleccionar. `play` corta lo que suene y,
 * con elecciones rápidas, sólo suena la última. Los demos se cachean por
 * persona (en memoria, mientras viva el hook) para no re-sintetizarlos.
 *
 * Demo del TONO (`playToneDemo`): elegir un tono hace sonar una frase neutra de
 * profesor (`TONE_DEMO_TEXT`) con la voz ACTUAL y ese tono — mismo player
 * (corta lo anterior, gana la última), cache por (voz, tono). Sólo con Gemini:
 * con ElevenLabs el tono no cambia la voz (sólo el prompt), así que no hay
 * demo (`toneDemoAvailable=false`). Con `opts.tone`, la demo de una VOZ también
 * suena con el tono actual (Gemini), para que se escuche como va a sonar.
 */
export interface UseCoachPickerReturn {
  coaches: ReadonlyArray<CoachCardModel>;
  selectedId: string | null;
  loading: boolean;
  error: string | null;
  /** voiceId de la voz activa (para previsualizar sugerencias de Inspire). */
  activeVoiceId: string | null;
  /** Estado de reproducción del preview ("Probar voz"). */
  previewState: AudioBtnState;
  /** id que se está previsualizando, para resaltar el botón correcto. */
  previewingId: string | null;
  select: (personaId: string) => void;
  /**
   * Elige el coach por defecto de una variedad de «Tu inglés» (misma regla que
   * el backend, `pickDefaultCoachForRegion`) — lo usa el chip «Tu inglés» cuando
   * el coach actual es de otro acento. No-op si el catálogo no tiene ninguno.
   */
  selectDefaultFor: (region: ConvRegion) => void;
  preview: (personaId: string) => void;
  /** ¿El tono cambia la voz? (Gemini sí; ElevenLabs no → sin demo de tono). */
  toneDemoAvailable: boolean;
  /** Tono cuyo demo se pidió último (para resaltar la tarjeta que suena). */
  demoTone: ToneId | null;
  /** Reproduce el demo del tono con la voz actual. No-op sin Gemini / sin voz. */
  playToneDemo: (tone: ToneId) => void;
  /** Variedad cuyo demo se pidió último (resalta la tarjeta que suena). */
  demoSpanishVariant: SpanishVariantId | null;
  /**
   * Reproduce el demo del "español del profe": una línea bilingüe en esa
   * variedad, con la voz y el tono actuales y el acento de la variedad
   * (Gemini). No-op sin Gemini / sin voz — mismo criterio que el tono.
   */
  playSpanishVariantDemo: (variant: SpanishVariantId) => void;
}

export interface UseCoachPickerOptions {
  /** Tono actual de la charla: la demo de voz (Gemini) suena con él. */
  tone?: ToneId;
  /**
   * «Tu inglés»: sin coach guardado, se muestra el coach por defecto de ESTA
   * variedad — el mismo que usa el backend en la sesión (encargo 111).
   * Obligatorio: sin la variedad, el selector mostraría un coach que la sesión
   * no usa.
   */
  region: ConvRegion;
}

interface VoiceChanger {
  sendVoiceChange: (voiceId: string, personaName: string) => void;
}

export function useCoachPicker(
  session: VoiceChanger,
  opts: UseCoachPickerOptions,
): UseCoachPickerReturn {
  const [coaches, setCoaches] = useState<ReadonlyArray<CoachCardModel>>([]);
  /** Elección explícita (guardada o hecha acá); `null` = el default de la variedad. */
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [defaultVoiceId, setDefaultVoiceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [ttsProvider, setTtsProvider] = useState<TtsProviderId | null>(null);
  const [demoTone, setDemoTone] = useState<ToneId | null>(null);
  const [demoSpanishVariant, setDemoSpanishVariant] = useState<SpanishVariantId | null>(null);
  const toneRef = useRef(opts.tone);
  toneRef.current = opts.tone;
  const { state: previewState, toggle, play } = useAudioBlob();
  /** Demo por persona (la promesa, así dos pedidos seguidos comparten el fetch). */
  const previewCacheRef = useRef(new Map<string, Promise<Blob>>());

  const sessionRef = useRef(session);
  sessionRef.current = session;

  // Carga inicial: catálogo + selección persistida del módulo.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        // El provider activo NO tiene fallback: sin él no sabemos qué voces
        // puede usar la sesión (fail-fast → estado de error del picker).
        const [personas, settings, activeTts] = await Promise.all([
          personasApi.list(),
          personasApi.settings().catch(() => null),
          personasApi.activeTts(MODULE_SLUG),
        ]);
        if (!alive) return;
        const catalog = buildCoachCatalog(personas, activeTts.provider);
        setCoaches(catalog);
        setTtsProvider(activeTts.provider);
        setDefaultVoiceId(activeTts.defaultVoiceId);
        const override = settings?.moduleOverrides?.[MODULE_SLUG];
        // Un override elegido bajo el OTRO provider no está en el catálogo: se
        // muestra el coach default de «Tu inglés» para el provider activo (el
        // mismo que usa el backend en la sesión). No se re-persiste: si el
        // módulo vuelve al otro provider, la elección vuelve.
        setChosenId(catalog.find((c) => c.id === override)?.id ?? null);
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : 'No pudimos cargar las voces.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const region = opts.region;
  const defaultFor = useCallback(
    (r: ConvRegion): CoachCardModel | null =>
      pickDefaultCoachForRegion(
        coaches.map((c) => ({ card: c, accent: c.accentKey, voiceId: c.voiceId })),
        defaultVoiceId,
        r,
      )?.card ?? null,
    [coaches, defaultVoiceId],
  );
  const selectedId = chosenId ?? defaultFor(region)?.id ?? coaches[0]?.id ?? null;

  const isGemini = ttsProvider === 'gemini';

  /** Demo cacheado por clave; un demo fallido no se queda (la próxima reintenta). */
  const cachedFetch = useCallback((key: string, fetcher: () => Promise<Blob>): Promise<Blob> => {
    const cache = previewCacheRef.current;
    const hit = cache.get(key);
    if (hit) return hit;
    const pending = fetcher();
    cache.set(key, pending);
    pending.catch(() => {
      if (cache.get(key) === pending) cache.delete(key);
    });
    return pending;
  }, []);

  const fetchPreview = useCallback(
    (personaId: string): Promise<Blob> => {
      // Con Gemini y un tono actual, la demo de la voz suena con ese tono.
      const tone = isGemini ? toneRef.current : undefined;
      return tone
        ? cachedFetch(`${personaId}:${tone}`, () => personasApi.preview(personaId, undefined, tone))
        : cachedFetch(personaId, () => personasApi.preview(personaId));
    },
    [cachedFetch, isGemini],
  );

  const playPreview = useCallback(
    (personaId: string): void => {
      setPreviewingId(personaId);
      setDemoTone(null);
      setDemoSpanishVariant(null);
      void play(() => fetchPreview(personaId));
    },
    [play, fetchPreview],
  );

  const playToneDemo = useCallback(
    (tone: ToneId): void => {
      if (!isGemini || selectedId === null) return;
      const personaId = selectedId;
      setPreviewingId(null);
      setDemoSpanishVariant(null);
      setDemoTone(tone);
      void play(() =>
        cachedFetch(`tone:${personaId}:${tone}`, () =>
          personasApi.preview(personaId, TONE_DEMO_TEXT, tone),
        ),
      );
    },
    [isGemini, selectedId, play, cachedFetch],
  );

  const playSpanishVariantDemo = useCallback(
    (variant: SpanishVariantId): void => {
      // Suena con el tono ACTUAL de la charla (misma identidad que la voz en
      // vivo); sin tono conocido no hay demo (nada de tono inventado).
      const tone = toneRef.current;
      if (!isGemini || selectedId === null || tone === undefined) return;
      const personaId = selectedId;
      setPreviewingId(null);
      setDemoTone(null);
      setDemoSpanishVariant(variant);
      void play(() =>
        cachedFetch(`es:${personaId}:${tone}:${variant}`, () =>
          personasApi.preview(personaId, SPANISH_VARIANT_DEMO_TEXT[variant], tone, variant),
        ),
      );
    },
    [isGemini, selectedId, play, cachedFetch],
  );

  const select = useCallback(
    (personaId: string): void => {
      const coach = coaches.find((c) => c.id === personaId);
      if (!coach) return;
      setChosenId(personaId);
      // Auto-play del demo: sale de ESTE handler (gesto del usuario).
      playPreview(personaId);
      // Cambio en caliente del TTS upstream (sin reiniciar la sesión).
      sessionRef.current.sendVoiceChange(coach.voiceId, coach.name);
      // Persistir el override del módulo (best-effort).
      void personasApi.setModule(MODULE_SLUG, personaId).catch(() => {
        /* best-effort: si falla, la voz igual cambió en caliente */
      });
    },
    [coaches, playPreview],
  );

  const selectDefaultFor = useCallback(
    (r: ConvRegion): void => {
      const coach = defaultFor(r);
      if (coach) select(coach.id);
    },
    [defaultFor, select],
  );

  // Botón explícito: play/pausa del demo de la voz que se está mostrando; si
  // suena el de OTRA voz, lo reemplaza en vez de sólo pararlo.
  const preview = useCallback(
    (personaId: string): void => {
      if (previewingId !== null && previewingId !== personaId) {
        playPreview(personaId);
        return;
      }
      setPreviewingId(personaId);
      void toggle(() => fetchPreview(personaId));
    },
    [toggle, fetchPreview, playPreview, previewingId],
  );

  const activeVoiceId = useMemo(
    () => coaches.find((c) => c.id === selectedId)?.voiceId ?? null,
    [coaches, selectedId],
  );

  return {
    coaches,
    selectedId,
    loading,
    error,
    activeVoiceId,
    previewState,
    previewingId,
    select,
    selectDefaultFor,
    preview,
    toneDemoAvailable: isGemini,
    demoTone,
    playToneDemo,
    demoSpanishVariant,
    playSpanishVariantDemo,
  };
}
